import React from 'react'
import PropTypes from 'prop-types'
import convert from 'htmr'
import cn from 'classnames'
import loadable from '@loadable/component'

import DefaultLayout from 'js/components/DefaultLayout'
import Logo from 'js/components/Logo'
import AggregateRating from 'js/components/AggregateRating/AggregateRating'
import Reviews from 'js/components/Reviews/Reviews'
import withSSR from 'js/components/withSSR/withSSR'
import ErrorPage from 'js/pages/ErrorPage'
import SubPageHeader from 'js/components/SubPageHeader'
import Mask from 'js/components/Mask'
import DatoRichText from 'js/components/DatoRichText/DatoRichText'
import Sticky from 'js/pages/DatoNativeAd/ReviewBlock'
import CompanyImprint from 'js/components/CompanyImprint/CompanyImprint'
import References from 'js/components/References/References'

import useBreakpoints from 'js/hooks/useBreakpoints'
import { mergeFallback, runQuery } from 'js/dato'
import { query } from 'js/pages/nativeAdQuery'

import datoLocales from 'js/datoLocales'
import NotMedicalAdviceDisclaimer from 'js/components/NotMedicalAdviceDisclaimer'
import { TrackScrollDepth } from 'js/analytics/components/TrackScrollDepth'

const DiscountBadge = loadable(() =>
  import(
    /* webpackChunkName: "DatoNativeAdDiscountBadge" */ 'js/pages/DatoNativeAd/DatoNativeAdDiscountBadge'
  ),
)
const DatoCTA = loadable(() =>
  import(/* webpackChunkName: "DatoCTA" */ 'js/components/DatoCTA/DatoCTA'),
)

const CheckMarks = ({ checkMarks }) => (
  <div
    className={cn(`
                  relative z-10
                  mb-32 rounded-8
                  bg-white p-24 md:mx-56
                  md:mb-48 md:mt-64 md:p-36 lg:mx-72 lg:px-48
                `)}
  >
    <ul className="c-checkmark-list">
      {checkMarks.map(({ text }) => (
        <li className="c-checkmark-list__item" key={text}>
          {text}
        </li>
      ))}
    </ul>
  </div>
)

CheckMarks.propTypes = {
  checkMarks: PropTypes.array,
}

function DatoNativeAd({ data: nativeAd = {}, isLoading, error }) {
  const { MD } = useBreakpoints()

  if (isLoading) {
    return (
      <DefaultLayout showInstallBanner={false}>
        <SubPageHeader loading={true} />
      </DefaultLayout>
    )
  }
  if (error) return <ErrorPage />

  const mainImageUrl =
    nativeAd.mainImage?.url || `${nativeAd.legacyMainImageUrl}?`

  const { x: fpX, y: fpY } = nativeAd.mainImage?.focalPoint || { x: 0, y: 0 }
  const mainImage = MD
    ? `${mainImageUrl}&w=1500&h=500&fp-x=${fpX}&fp-y=${fpY}&auto=format`
    : `${mainImageUrl}&fit=crop&w=375&h=400&fp-x=${fpX}&fp-y=${fpY}&auto=format`

  return (
    <>
      <TrackScrollDepth />
      <DefaultLayout showInstallBanner={false}>
        <div className="c-native-ad flex flex-col">
          <Sticky {...nativeAd} />
          <Mask>
            <div className="relative">
              <div
                className={cn(`
                bg-cover pb-96 pt-56 text-center text-white
                lg:pb-128 lg:pt-72 xxl:py-192
              `)}
                style={{
                  background: `linear-gradient(
                    rgba(19, 20, 27, 0.3), 
                    rgba(19, 20, 27, 0.6)
                  ) repeat scroll 0% 0%, 
                  rgba(0, 0, 0, 0) url(${mainImage && mainImage})`,
                }}
              >
                {nativeAd.discount && nativeAd.couponCode && (
                  <DiscountBadge couponCode={nativeAd.couponCode} />
                )}
                <h1
                  className={cn(`
                  type-large bolden-children
                  mx-auto my-32 w-full
                  max-w-968 px-16
                  md:my-40 md:px-32 xxl:type-64
                `)}
                >
                  {convert(nativeAd.mainHeading, {
                    transform: {
                      // eslint-disable-next-line react/prop-types
                      h1: function Heading1({ children }) {
                        return children
                      },
                    },
                  })}
                </h1>
                {nativeAd.showAppButtons && (
                  <div className="mx-auto max-w-968 px-16 md:px-32">
                    <DatoCTA {...nativeAd.cta} onDark />
                  </div>
                )}
              </div>
            </div>
          </Mask>
          <div className="mx-auto w-full max-w-968 px-16 md:px-32">
            <div className="relative mx-auto max-w-1024 lg:max-w-1440">
              {nativeAd.checkmarkList.length > 0 && (
                <div className="-mt-40 md:-mt-128">
                  <CheckMarks checkMarks={nativeAd.checkmarkList} />
                </div>
              )}
              <DatoRichText
                className="mx-auto w-full px-16 md:px-32"
                richText={nativeAd.content}
                cta={nativeAd.cta}
              />
            </div>
            <div className="text-center">
              {nativeAd.callout && (
                <h2 className="mb-32 type-28-demibold md:mb-40 md:type-40">
                  {nativeAd.callout}
                </h2>
              )}
              {nativeAd.showAppButtons && (
                <div className="mb-32 text-center lg:mb-80">
                  <DatoCTA {...nativeAd.cta} />
                </div>
              )}
              {nativeAd.showAppRating && (
                <div className="mb-32 lg:mb-48">
                  <AggregateRating />
                </div>
              )}
            </div>
          </div>
          <div className="mx-auto w-full max-w-968 px-16 md:px-32 lg:max-w-1280">
            {nativeAd.showReviews && (
              <div className="mb-32 lg:mb-48">
                <Reviews />
              </div>
            )}
          </div>
        </div>
        <div className="my-36 text-center">
          <References references={nativeAd.references} />
          <NotMedicalAdviceDisclaimer className="mx-auto mb-32 max-w-968 px-16 md:px-32 lg:mb-48" />
          <Logo />
          <CompanyImprint />
        </div>
      </DefaultLayout>
    </>
  )
}

DatoNativeAd.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

DatoNativeAd.getInitialProps = ({ isPreview, params, language }) => {
  const slug = params.slug.replace('-dato', '').toLowerCase()
  const locale = datoLocales[language]

  return runQuery(query, { slug, locale }, isPreview).then(
    ({ page, fallback }) => {
      if (!page) {
        return
      }

      return mergeFallback(page, fallback)
    },
  )
}

export default withSSR(DatoNativeAd)
